import React, {
  useRef,
  useEffect,
  Fragment,
  cloneElement,
} from 'react';

import { useForkRef, useEventCallback } from '../../hooks';
import { ownerDocument, clickedRootScrollbar } from '../../utils/document';

import { mapEventPropToEvent } from './utils';

const ClickAwayListener = ({
  children,
  disableReactTree = false,
  mouseEvent = 'onClick',
  onClickAway,
  touchEvent = 'onTouchEnd',
}) => {
  const movedRef = useRef(false);
  const nodeRef = useRef(null);
  const activatedRef = useRef(false);
  const syntheticEventRef = useRef(false);

  useEffect(() => {
    setTimeout(() => {
      activatedRef.current = true;
    }, 0);

    return () => {
      activatedRef.current = false;
    };
  }, []);

  const handleRef = useForkRef(
    children.ref,
    nodeRef,
  );

  const handleClickAway = useEventCallback((event) => {
    const insideReactTree = syntheticEventRef.current;
    syntheticEventRef.current = false;

    const doc = ownerDocument(nodeRef.current);

    if (
      !activatedRef.current ||
      !nodeRef.current ||
      ('clientX' in event && clickedRootScrollbar(event, doc))
    ) {
      return;
    }

    if (movedRef.current) {
      movedRef.current = false;
      return;
    }

    let insideDom;

    if (event.composedPath) {
      insideDom = event.composedPath().indexOf(nodeRef.current) > -1;
    } else {
      insideDom =
        !doc.documentElement.contains(
          event.target,
        ) ||
        nodeRef.current.contains(
          event.target,
        );
    }

    if (!insideDom && (disableReactTree || !insideReactTree)) {
      onClickAway(event);
    }
  });

  const createHandleSynthetic =
    (handlerName) =>
    (event) => {
      syntheticEventRef.current = true;

      const childrenPropsHandler = children.props[handlerName];

      if (childrenPropsHandler) {
        childrenPropsHandler(event);
      }
    };

  const childrenProps = { ref: handleRef };

  if (touchEvent !== false) {
    childrenProps[touchEvent] = createHandleSynthetic(touchEvent);
  }

  useEffect(() => {
    if (touchEvent !== false) {
      const mappedTouchEvent = mapEventPropToEvent(touchEvent);
      const doc = ownerDocument(nodeRef.current);

      const handleTouchMove = () => {
        movedRef.current = true;
      };

      doc.addEventListener(mappedTouchEvent, handleClickAway);
      doc.addEventListener('touchmove', handleTouchMove);

      return () => {
        doc.removeEventListener(mappedTouchEvent, handleClickAway);
        doc.removeEventListener('touchmove', handleTouchMove);
      };
    }

    return undefined;
  }, [handleClickAway, touchEvent]);

  if (mouseEvent !== false) {
    childrenProps[mouseEvent] = createHandleSynthetic(mouseEvent);
  }

  useEffect(() => {
    if (mouseEvent !== false) {
      const mappedMouseEvent = mapEventPropToEvent(mouseEvent);
      const doc = ownerDocument(nodeRef.current);

      doc.addEventListener(mappedMouseEvent, handleClickAway);

      return () => {
        doc.removeEventListener(mappedMouseEvent, handleClickAway);
      };
    }

    return undefined;
  }, [handleClickAway, mouseEvent]);

  return <Fragment>{cloneElement(children, childrenProps)}</Fragment>;
};

export default ClickAwayListener;
